import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import {
  Home as HomeIcon,
  People as PeopleIcon,
  RateReview as ReviewIcon,
  Event as LeaveIcon,
  LocalHospital as SickLeaveIcon,
  Assessment as ReportIcon,
  Settings as SettingsIcon,
  Article as ArticleIcon,
  Computer as EquipmentIcon,
  Schedule as ShortAbsenceIcon,
  CardMembership as LicenseIcon,
  Policy as PolicyIcon,
  AccountTree as AccountTreeIcon
} from '@mui/icons-material';
import './Sidebar.css';
import { usePermissions } from './PermissionsContext';
import HelpModal from './Help/HelpModal';

// Persisted open/closed state for the collapsible top-level groups.
const SIDEBAR_STATE_KEY = 'hrapp.sidebar.collapsed.v1';

const Sidebar = ({ collapsed = false }) => {
  const location = useLocation();
  const { isSystemAdmin, roles, hasPermission } = usePermissions();
  const [helpModalOpen, setHelpModalOpen] = React.useState(false);

  // Per-group open state. Missing key ⇒ open (default ALL open on fresh load).
  const [openState, setOpenState] = React.useState(() => {
    try {
      const raw = localStorage.getItem(SIDEBAR_STATE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const isOpen = (name) => openState[name] !== false;
  const toggleGroup = (name) => {
    setOpenState((prev) => {
      const next = { ...prev, [name]: prev[name] === false ? true : false };
      try { localStorage.setItem(SIDEBAR_STATE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  // ── Group definitions ──────────────────────────────────────────────────────
  const dashboardItems = [
    { text: 'Dashboard', icon: <HomeIcon />, path: '/', roles: ['Employee','HRManager','SystemAdmin','DepartmentManager','BoardManager','OperationManager','OfficeAdministrator'] },
  ];

  // Employee section — gains "My Timesheet" (moved out of Reports).
  const employeeMenuItems = [
    { text: 'Leave Requests', icon: <LeaveIcon />, path: '/leave', roles: ['Employee','SystemAdmin','DepartmentManager','BoardManager','OperationManager','OfficeAdministrator'] },
    { text: 'Sick Leave', icon: <SickLeaveIcon />, path: '/sick-leave', roles: ['Employee','SystemAdmin','DepartmentManager','BoardManager','OperationManager','OfficeAdministrator'] },
    { text: 'Short Absences', icon: <ShortAbsenceIcon />, path: '/short-absences', roles: ['Employee','SystemAdmin','DepartmentManager','BoardManager','OperationManager','OfficeAdministrator'] },
    { text: 'My Equipment', icon: <EquipmentIcon />, path: '/equipment', roles: ['Employee','SystemAdmin','DepartmentManager','BoardManager','OperationManager','OfficeAdministrator'] },
    { text: 'My Profile', icon: <PeopleIcon />, path: '/profile', roles: ['Employee','SystemAdmin','DepartmentManager','BoardManager','OperationManager','OfficeAdministrator'] },
    { text: 'Company Docs', icon: <PolicyIcon />, path: '/company-documents', roles: ['Employee','SystemAdmin','DepartmentManager','BoardManager','OperationManager','OfficeAdministrator','HR','HRManager'] },
    { text: 'My Timesheet', icon: <ReportIcon />, path: '/timesheet/my', permission: 'Timesheet.ViewOwn' },
    { text: 'My Projects', icon: <AccountTreeIcon />, path: '/my-projects', permission: 'Timesheet.ViewOwn' },
  ];

  const actionsMenuItems = [
    { text: 'Leave Approvals', icon: <ReviewIcon />, path: '/leave-approvals', roles: ['DepartmentManager','BoardManager','SystemAdmin'] },
    { text: 'Short Absence Approvals', icon: <ShortAbsenceIcon />, path: '/short-absence-approvals', roles: ['DepartmentManager','BoardManager','SystemAdmin'] },
    { text: 'Equipment Approvals', icon: <EquipmentIcon />, path: '/equipment-approvals', roles: ['DepartmentManager','BoardManager','SystemAdmin'] },
    { text: 'Equipment Orders', icon: <EquipmentIcon />, path: '/equipment-orders', roles: ['OperationManager','SystemAdmin'] },
    { text: 'Equipment Inventory', icon: <EquipmentIcon />, path: '/admin/equipment-inventory', roles: ['OperationManager','OfficeAdministrator','SystemAdmin'] },
    { text: 'Equipment Assignments', icon: <EquipmentIcon />, path: '/admin/equipment-assignments', roles: ['OperationManager','OfficeAdministrator','SystemAdmin'] },
    { text: 'License Assignments', icon: <LicenseIcon />, path: '/admin/license-assignments', roles: ['OperationManager','OfficeAdministrator','SystemAdmin'] },
  ];

  // HR group: both former items (Employee File, Document Types) moved into
  // Administration, so this is empty and the group won't render (hide-if-empty).
  const hrMenuItems = [];

  // Projects group — gains the four project/team reports (moved out of Reports).
  const projectsMenuItems = [
    { text: 'Projects', icon: <AccountTreeIcon />, path: '/projects', roles: ['SystemAdmin'] },
    { text: 'All Projects Review', icon: <ReportIcon />, path: '/reports/projects', permission: 'Timesheet.ViewProjectReports' },
    { text: 'Project Consumption', icon: <ReportIcon />, path: '/reports/project-consumption', permission: 'Timesheet.ViewProjectReports' },
    { text: 'Project Tasks Review', icon: <ReportIcon />, path: '/reports/project-tasks', permission: 'Timesheet.ViewProjectReports' },
    { text: 'Team Utilization', icon: <ReportIcon />, path: '/reports/team-utilization', permission: 'Timesheet.ViewProjectReports' },
    { text: 'Team Calendar Overview', icon: <ReportIcon />, path: '/reports/team-calendar', permission: 'Timesheet.ViewProjectReports' },
  ];

  // Reports group — leave-related reports only.
  const reportsMenuItems = [
    { text: 'Leave Consumption Report', icon: <ReportIcon />, path: '/admin/leave-consumption-report', roles: ['SystemAdmin'] },
    { text: 'Annual Leave Balance Report', icon: <ReportIcon />, path: '/admin/annual-leave-report', roles: ['SystemAdmin','OperationManager','BoardManager'] },
    { text: 'Paid Leave Report', icon: <ReportIcon />, path: '/admin/paid-leave-report', roles: ['SystemAdmin','HRManager','OperationManager','BoardManager'] },
    // S3 — contracted salaries; visible with the ViewContracts permission
    { text: 'Salary Register', icon: <ReportIcon />, path: '/reports/salary-register', permission: 'ViewContracts' },
    { text: 'Sick Leave Report', icon: <SickLeaveIcon />, path: '/admin/sick-leave-report', roles: ['SystemAdmin','BoardManager'] },
    { text: 'Short Absence Report', icon: <ShortAbsenceIcon />, path: '/admin/short-absence-report', roles: ['SystemAdmin','BoardManager'] },
  ];

  // Administration — one collapsible group with labelled sub-sections (the
  // sub-sections themselves do not collapse independently).
  const administrationSubsections = [
    { label: 'People', items: [
      { text: 'Person Records', icon: <PeopleIcon />, path: '/people/new', roles: ['SystemAdmin'] },
      { text: 'Identity & Access', icon: <SettingsIcon />, path: '/admin/identity-access', roles: ['SystemAdmin'] },
      { text: 'Employee File', icon: <PeopleIcon />, path: '/admin/employee-case', roles: ['SystemAdmin','HRManager','BoardManager'] },
    ]},
    { label: 'Timesheet', items: [
      { text: 'Month Locks', icon: <SettingsIcon />, path: '/admin/month-locks', permission: 'Timesheet.ManageMonthLocks' },
      { text: 'Hours Audit Log', icon: <SettingsIcon />, path: '/admin/hours-audit', permission: 'Timesheet.ViewHoursAudit' },
    ]},
    { label: 'HR Master Data', items: [
      { text: 'Seniority Levels', icon: <SettingsIcon />, path: '/settings/seniority-levels', roles: ['SystemAdmin'] },
      { text: 'Positions', icon: <SettingsIcon />, path: '/settings/positions', roles: ['SystemAdmin'] },
      { text: 'Departments', icon: <SettingsIcon />, path: '/settings/departments', roles: ['SystemAdmin'] },
      { text: 'Contract Types', icon: <SettingsIcon />, path: '/settings/contract-types', roles: ['SystemAdmin'] },
      { text: 'Document Types', icon: <SettingsIcon />, path: '/settings/document-types', roles: ['SystemAdmin','HRManager'] },
      { text: 'Career Event Types', icon: <SettingsIcon />, path: '/settings/career-event-types', roles: ['SystemAdmin'] },
    ]},
    { label: 'Skills', items: [
      { text: 'Skills', icon: <SettingsIcon />, path: '/settings/skills', roles: ['SystemAdmin'] },
      { text: 'Skill Categories', icon: <SettingsIcon />, path: '/settings/skill-categories', roles: ['SystemAdmin'] },
      { text: 'Position Skill Sets', icon: <SettingsIcon />, path: '/settings/position-skill-sets', roles: ['SystemAdmin'] },
    ]},
    { label: 'Leave & Absence', items: [
      { text: 'Leave Approvers', icon: <SettingsIcon />, path: '/settings/leave-approvers', roles: ['SystemAdmin'] },
      { text: 'Leave Balances', icon: <SettingsIcon />, path: '/settings/leave-balances', roles: ['SystemAdmin'] },
      { text: 'Initial Leave Entry', icon: <SettingsIcon />, path: '/settings/initial-leave-entry', roles: ['SystemAdmin'] },
      { text: 'Profile Field Visibility', icon: <SettingsIcon />, path: '/settings/profile-visibility', roles: ['SystemAdmin'] },
    ]},
    { label: 'Equipment & Licenses', items: [
      { text: 'Equipment Categories', icon: <SettingsIcon />, path: '/settings/equipment-categories', roles: ['SystemAdmin'] },
      { text: 'Equipment Item Types', icon: <SettingsIcon />, path: '/settings/equipment-item-types', roles: ['SystemAdmin'] },
      { text: 'License Vendors', icon: <SettingsIcon />, path: '/settings/license-vendors', roles: ['SystemAdmin','OperationManager','OfficeAdministrator'] },
      { text: 'License Products', icon: <SettingsIcon />, path: '/settings/license-products', roles: ['SystemAdmin','OperationManager','OfficeAdministrator'] },
    ]},
    { label: 'System', items: [
      { text: 'Notifications', icon: <SettingsIcon />, path: '/settings/notifications', roles: ['SystemAdmin'] },
    ]},
  ];

  const isActive = (path) => location.pathname === path;

  // Unified visibility: permission-gated items check hasPermission; role-gated
  // items pass for SystemAdmin or any matching role.
  const isItemVisible = (item) => {
    if (item.permission) return !!hasPermission?.(item.permission);
    if (item.roles) return isSystemAdmin || item.roles.some((r) => roles?.includes(r));
    return true;
  };
  const filterItems = (items) => items.filter(isItemVisible);

  const renderNavItem = (item) => {
    const content = (
      <ListItem
        key={item.text}
        component={Link}
        to={item.path}
        className={`nav-item ${isActive(item.path) ? 'active' : ''} ${collapsed ? 'collapsed' : ''}`}
      >
        <ListItemIcon className="nav-icon">{item.icon}</ListItemIcon>
        {!collapsed && <ListItemText primary={item.text} className="nav-text" />}
      </ListItem>
    );
    return collapsed ? (
      <Tooltip key={item.text} title={item.text} placement="right" arrow>{content}</Tooltip>
    ) : content;
  };

  // Clickable, keyboard-operable collapsible group header.
  const renderGroupHeader = (name) => {
    const open = isOpen(name);
    return (
      <div
        className="nav-group-header"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-controls={`nav-group-body-${name}`}
        data-testid={`nav-group-header-${name}`}
        onClick={() => toggleGroup(name)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleGroup(name); }
        }}
      >
        <span className="nav-group-chevron" aria-hidden="true">{open ? '▼' : '▶'}</span>
        <Typography variant="overline" className="nav-title">{name}</Typography>
      </div>
    );
  };

  // A standard collapsible group. In icon-only (collapsed) mode there is no
  // header and items always render.
  const renderGroup = (name, items) => {
    if (items.length === 0) return null;
    if (collapsed) {
      return (
        <Box className="nav-group" key={name}>
          <List className="nav-list">{items.map(renderNavItem)}</List>
        </Box>
      );
    }
    return (
      <Box className="nav-group" key={name}>
        {renderGroupHeader(name)}
        {isOpen(name) && (
          <List className="nav-list" role="group" id={`nav-group-body-${name}`} data-testid={`nav-group-body-${name}`}>
            {items.map(renderNavItem)}
          </List>
        )}
      </Box>
    );
  };

  // Administration: collapsible group whose body is a set of labelled
  // sub-sections. A sub-section hides when it has no visible items.
  const renderAdministration = (subsections) => {
    if (subsections.length === 0) return null;
    if (collapsed) {
      return (
        <Box className="nav-group">
          <List className="nav-list">{subsections.flatMap((s) => s.items).map(renderNavItem)}</List>
        </Box>
      );
    }
    return (
      <Box className="nav-group">
        {renderGroupHeader('Administration')}
        {isOpen('Administration') && (
          <div role="group" id="nav-group-body-Administration" data-testid="nav-group-body-Administration">
            {subsections.map((sub) => (
              <div className="nav-subsection" key={sub.label} data-testid={`nav-subsection-${sub.label}`}>
                <div className="nav-subsection-title">{sub.label}</div>
                <List className="nav-list">{sub.items.map(renderNavItem)}</List>
              </div>
            ))}
          </div>
        )}
      </Box>
    );
  };

  const dashboardVisible = filterItems(dashboardItems);
  const employeeVisible = filterItems(employeeMenuItems);
  const actionsVisible = filterItems(actionsMenuItems);
  const hrVisible = filterItems(hrMenuItems);
  const projectsVisible = filterItems(projectsMenuItems);
  const reportsVisible = filterItems(reportsMenuItems);
  const adminSubsVisible = administrationSubsections
    .map((s) => ({ label: s.label, items: filterItems(s.items) }))
    .filter((s) => s.items.length > 0);

  const helpButton = (
    <ListItem button onClick={() => setHelpModalOpen(true)} className="nav-item">
      <ListItemIcon className="nav-icon"><ArticleIcon /></ListItemIcon>
      {!collapsed && <ListItemText primary="User Guide" className="nav-text" />}
    </ListItem>
  );

  return (
    <Box className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      {renderGroup('Dashboard', dashboardVisible)}
      {renderGroup('Employee', employeeVisible)}
      {renderGroup('Actions', actionsVisible)}
      {renderGroup('HR', hrVisible)}
      {renderGroup('Projects', projectsVisible)}
      {renderGroup('Reports', reportsVisible)}
      {renderAdministration(adminSubsVisible)}

      {/* Help — pinned to the bottom, also collapsible */}
      <Box className="nav-group" sx={{ mt: 'auto', borderTop: '1px solid rgba(255,255,255,0.1)', pt: 1 }}>
        {collapsed ? (
          <List className="nav-list">{helpButton}</List>
        ) : (
          <>
            {renderGroupHeader('Help')}
            {isOpen('Help') && (
              <List className="nav-list" role="group" id="nav-group-body-Help" data-testid="nav-group-body-Help">
                {helpButton}
              </List>
            )}
          </>
        )}
      </Box>

      <HelpModal
        open={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
        helpType="leave-guide"
      />
    </Box>
  );
};

export default Sidebar;
